'use client';

import {
  TARGET_LONG_EDGE_BY_TIER,
  UPSCALER_CUSTOM_SIZE_FIELD,
  UPSCALER_MAX_LONG_EDGE,
  UPSCALER_PRINT_DPI,
  UPSCALER_PRINT_PRESETS,
  type UpscalerSizeUnit,
  calculateUpscalerOutputSize,
  getUpscalerMaxFactor,
  getUpscalerRealEsrganMaxFactor,
  getUpscalerRealEsrganScale,
  getUpscalerRequestedLongEdge,
  getUpscalerSizeTier,
  getUpscalerSourceDimensions,
  pixelsToSize,
  sizeToPixels,
} from '@/ai/image/tool-contracts/upscaler/output-size';

import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { X } from '@/components/icons/app-icons';
import { PremiumCrownIcon } from '@/components/paid-access/premium-crown-icon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/forms/meta/select';
import { toast } from '@/lib/ui/toast';
import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  WorkspaceToolParamValue,
  WorkspaceToolParamView,
} from '../contracts/tool-view';
import { WorkspaceImageInfoCard } from './workspace-image-info-card';
import { WorkspaceInfoTooltip } from './workspace-info-tooltip';
import { WorkspaceMobileDrawer } from './workspace-mobile-drawer';
import { useWorkspaceIsMobileViewport } from './workspace-viewport';

type Dimensions = {
  width: number;
  height: number;
  hasTransparentPixels?: boolean;
};
function readDimensions(value: unknown): Dimensions | null {
  if (
    !value ||
    typeof value !== 'object' ||
    !('width' in value) ||
    !('height' in value)
  )
    return null;
  return typeof value.width === 'number' &&
    typeof value.height === 'number' &&
    value.width > 0 &&
    value.height > 0
    ? {
        width: value.width,
        height: value.height,
        hasTransparentPixels: hasTransparentPixels(value),
      }
    : null;
}

function hasTransparentPixels(value: unknown) {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as { hasTransparentPixels?: unknown }).hasTransparentPixels === true
  );
}
const format = (value: number) => String(Math.round(value * 100) / 100);

export function WorkspaceUpscaleSize({
  param,
  values,
  imageMeta,
  inputImageUrl,
  resultImageUrl,
  resultImageDimensions,
  onApply,
  onValidityChange,
  showPaidFeatureMarkers,
  premiumFeatureTooltip,
  imageInfoContainer,
}: {
  param: WorkspaceToolParamView;
  values: Record<string, WorkspaceToolParamValue>;
  imageMeta: unknown;
  inputImageUrl?: string | null;
  resultImageUrl?: string | null;
  resultImageDimensions?: Pick<Dimensions, 'width' | 'height'> | null;
  onApply: (values: Record<string, WorkspaceToolParamValue>) => boolean;
  onValidityChange?: (valid: boolean) => void;
  showPaidFeatureMarkers?: boolean;
  premiumFeatureTooltip?: string;
  imageInfoContainer?: HTMLElement | null;
}) {
  const copy = param.upscaleSizeCopy;
  if (!copy) throw new Error('Upscale size copy is missing.');
  const renderUnavailableHint = () => (
    <WorkspaceInfoTooltip
      className="cms-workspace-upscale-size__unavailableHint"
      icon={renderAppIcon({
        name: 'forbid-circle',
        className: 'cms-workspace-upscale-size__unavailableHintIcon',
      })}
      text={copy.sizeOptionUnavailableTooltip}
    />
  );
  const id = useId();
  const [sizeMode, setSizeMode] = useState<'target' | 'factor'>(
    values[UPSCALER_CUSTOM_SIZE_FIELD] ? 'factor' : 'target'
  );
  const [pendingFactor, setPendingFactor] = useState<number | null>(null);
  const [editingFactor, setEditingFactor] = useState(false);
  const factorInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editingFactor) {
      factorInputRef.current?.focus();
      factorInputRef.current?.select();
    }
  }, [editingFactor]);
  const [factorDraft, setFactorDraft] = useState<string | null>(null);
  const [mode, setMode] = useState<'custom' | 'print'>('custom');
  const [open, setOpen] = useState(false);
  const isMobile = useWorkspaceIsMobileViewport();
  const rootRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDialogElement>(null);
  const automaticAdjustmentRef = useRef<string | null>(null);
  const [popoverTop, setPopoverTop] = useState<number | null>(null);
  useEffect(() => {
    if (!open || isMobile) {
      setPopoverTop(null);
      return;
    }
    const root = rootRef.current;
    const popover = popoverRef.current;
    if (!root || !popover) return;
    const position = () => {
      const gap = Number.parseFloat(getComputedStyle(root).rowGap) || 0;
      const top = Math.max(
        gap,
        Math.min(
          root.getBoundingClientRect().top,
          window.innerHeight - popover.getBoundingClientRect().height - gap
        )
      );
      setPopoverTop(top);
    };
    position();
    const observer = new ResizeObserver(position);
    observer.observe(popover);
    observer.observe(root);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [open, isMobile]);
  const closePanel = () => {
    setOpen(false);
    setDraft(null);
    setError(null);
    triggerRef.current?.focus();
  };
  useEffect(() => {
    if (!open || isMobile) return;
    const pointer = (event: PointerEvent) => {
      if (rootRef.current?.querySelector('[role="listbox"]')) return;
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setDraft(null);
        setError(null);
      }
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setDraft(null);
        setError(null);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('keydown', key);
    };
  }, [open, isMobile]);
  const [unit, setUnit] = useState<UpscalerSizeUnit>('px');
  const [draft, setDraft] = useState<{
    axis: 'width' | 'height';
    text: string;
  } | null>(null);
  const [presetId, setPresetId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    url: string;
    settings: string;
    loading: boolean;
    dimensions: Dimensions | null;
  } | null>(null);
  const original = readDimensions(imageMeta);
  const settings = JSON.stringify([
    inputImageUrl,
    original?.width,
    original?.height,
    Object.entries(values).sort(([a], [b]) => a.localeCompare(b)),
  ]);
  const showActual = Boolean(
    resultImageUrl &&
      result?.url === resultImageUrl &&
      result.settings === settings
  );
  const actualResultDimensions =
    readDimensions(resultImageDimensions) ?? result?.dimensions ?? null;
  const density = UPSCALER_PRINT_DPI;
  const source = original && getUpscalerSourceDimensions(values, original);
  const output = source ? calculateUpscalerOutputSize(values, source) : null;
  const requested =
    values[UPSCALER_CUSTOM_SIZE_FIELD] || !output
      ? getUpscalerRequestedLongEdge(values)
      : Math.max(output.width, output.height);
  const transparentInput = hasTransparentPixels(imageMeta);
  const maxFactor =
    original && source
      ? transparentInput
        ? getUpscalerRealEsrganMaxFactor(original)
        : getUpscalerMaxFactor(original, source, values)
      : 0;
  const isTargetOptionUnavailable = (
    option: NonNullable<WorkspaceToolParamView['options']>[number]
  ) => {
    if (option.disabled || !original || typeof option.value !== 'string') {
      return option.disabled === true;
    }
    const candidateValues = {
      ...values,
      [param.id]: option.value,
      [UPSCALER_CUSTOM_SIZE_FIELD]: 0,
    };
    if (transparentInput) {
      return (
        maxFactor < 1 ||
        getUpscalerRealEsrganScale(candidateValues, original) > maxFactor
      );
    }
    const candidateSource = getUpscalerSourceDimensions(
      candidateValues,
      original
    );
    return calculateUpscalerOutputSize(candidateValues, candidateSource)
      .exceedsLimit;
  };
  const getFallbackTargetOption = (
    selectedOption?: NonNullable<WorkspaceToolParamView['options']>[number]
  ) => {
    const options = param.options ?? [];
    const selectedIndex = selectedOption
      ? options.indexOf(selectedOption)
      : options.length;
    return options
      .slice(0, selectedIndex < 0 ? options.length : selectedIndex)
      .filter((option) => !isTargetOptionUnavailable(option))
      .at(-1);
  };
  const customLongEdge =
    typeof values[UPSCALER_CUSTOM_SIZE_FIELD] === 'number'
      ? values[UPSCALER_CUSTOM_SIZE_FIELD]
      : 0;
  const automaticAdjustment = (() => {
    if (!original || maxFactor < 1) return null;
    if (customLongEdge > 0) {
      const maximumLongEdge = Math.floor(
        Math.max(original.width, original.height) * maxFactor
      );
      if (customLongEdge <= maximumLongEdge) return null;
      return {
        key: JSON.stringify([
          inputImageUrl,
          original.width,
          original.height,
          customLongEdge,
          maximumLongEdge,
          transparentInput,
        ]),
        mode: 'factor' as const,
        label: `${format(maxFactor)}×`,
        values: {
          ...values,
          ...(transparentInput ? { scene: 'general', highQuality: false } : {}),
          [param.id]: getUpscalerSizeTier(maximumLongEdge),
          [UPSCALER_CUSTOM_SIZE_FIELD]: maximumLongEdge,
        },
      };
    }
    const selectedOption = (param.options ?? []).find((option) =>
      Object.is(option.value, values[param.id])
    );
    if (!selectedOption || !isTargetOptionUnavailable(selectedOption)) {
      return null;
    }
    const fallback = getFallbackTargetOption(selectedOption);
    if (!fallback) return null;
    return {
      key: JSON.stringify([
        inputImageUrl,
        original.width,
        original.height,
        selectedOption.value,
        fallback.value,
        transparentInput,
      ]),
      mode: 'target' as const,
      label: fallback.label,
      values: {
        ...values,
        ...(transparentInput ? { scene: 'general', highQuality: false } : {}),
        [param.id]: fallback.value,
        [UPSCALER_CUSTOM_SIZE_FIELD]: 0,
      },
    };
  })();
  useEffect(() => {
    if (!automaticAdjustment) {
      automaticAdjustmentRef.current = null;
      return;
    }
    if (automaticAdjustmentRef.current === automaticAdjustment.key) return;
    automaticAdjustmentRef.current = automaticAdjustment.key;
    if (!onApply(automaticAdjustment.values)) return;
    setSizeMode(automaticAdjustment.mode);
    setDraft(null);
    setError(null);
    setPresetId('');
    toast.info(
      copy.factorAdjusted.replace('{factor}', automaticAdjustment.label)
    );
  }, [automaticAdjustment, copy.factorAdjusted, onApply]);
  const transparentLimitExceeded = Boolean(
    transparentInput &&
      original &&
      (maxFactor < 1 ||
        getUpscalerRealEsrganScale(values, original) > maxFactor)
  );
  const exceedsLimit =
    output?.exceedsLimit === true || transparentLimitExceeded;
  const valid = Boolean(original && !error && !exceedsLimit && !editingFactor);
  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);
  useEffect(() => () => onValidityChange?.(true), [onValidityChange]);
  useEffect(() => {
    if (!resultImageUrl) return;
    let active = true;
    // Capture settings when a new result arrives; later edits should show the next estimate.
    setResult({
      url: resultImageUrl,
      settings,
      loading: true,
      dimensions: null,
    });
    const img = new Image();
    img.onload = () => {
      if (active)
        setResult({
          url: resultImageUrl,
          settings,
          loading: false,
          dimensions: readDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
          }),
        });
    };
    img.onerror = () => {
      if (active)
        setResult({
          url: resultImageUrl,
          settings,
          loading: false,
          dimensions: null,
        });
    };
    img.src = resultImageUrl;
    return () => {
      active = false;
      img.onload = null;
      img.onerror = null;
    };
  }, [resultImageUrl]);
  useEffect(() => {
    setDraft(null);
    setError(null);
    setPresetId('');
  }, [inputImageUrl]);

  const commit = (shortEdge: number, custom = true) => {
    if (!Number.isFinite(shortEdge) || shortEdge <= 0) {
      setError(copy.invalid);
      return false;
    }
    const pixels = Math.ceil(shortEdge);
    if (pixels > UPSCALER_MAX_LONG_EDGE) {
      setError(copy.limit);
      return false;
    }
    const nextValues = {
      ...values,
      ...(transparentInput ? { scene: 'general', highQuality: false } : {}),
      [param.id]: getUpscalerSizeTier(pixels),
      [UPSCALER_CUSTOM_SIZE_FIELD]: custom ? pixels : 0,
    };
    const transparentLimitExceeded = Boolean(
      transparentInput &&
        original &&
        (maxFactor < 1 ||
          getUpscalerRealEsrganScale(nextValues, original) > maxFactor)
    );
    if (
      transparentLimitExceeded ||
      (source &&
        calculateUpscalerOutputSize(
          nextValues,
          values.highQuality === true && original
            ? getUpscalerSourceDimensions(nextValues, original)
            : source
        ).exceedsLimit)
    ) {
      setError(copy.limit);
      return false;
    }
    const applied = onApply(nextValues);
    setError(null);
    if (!applied) setDraft(null);
    if (applied) {
      setPendingFactor(null);
      setSizeMode(custom ? 'factor' : 'target');
      setFactorDraft(null);
    }
    return applied;
  };
  const applyPreset = (nextId: string) => {
    const preset = UPSCALER_PRINT_PRESETS.find((item) => item.id === nextId);
    if (!original || !preset) return;
    const nextSwap =
      original.width > original.height !== preset.width > preset.height;
    const w = sizeToPixels(
      nextSwap ? preset.height : preset.width,
      preset.unit,
      density
    );
    const h = sizeToPixels(
      nextSwap ? preset.width : preset.height,
      preset.unit,
      density
    );
    if (
      commit(
        Math.min(w / original.width, h / original.height) *
          Math.max(original.width, original.height)
      )
    ) {
      setPresetId(nextId);
      setDraft(null);
    }
  };
  const editDimension = (axis: 'width' | 'height', text: string) => {
    setDraft({ axis, text });
    if (!original || !text.trim() || Number(text) <= 0) {
      setError(copy.invalid);
      return;
    }
    const pixels = sizeToPixels(Number(text), unit, density);
    commit(
      (pixels / original[axis]) * Math.max(original.width, original.height)
    );
  };
  const desired = original
    ? {
        width:
          (requested * original.width) /
          Math.max(original.width, original.height),
        height:
          (requested * original.height) /
          Math.max(original.width, original.height),
      }
    : null;
  const fieldValue = (axis: 'width' | 'height') => {
    if (draft?.axis === axis) return draft.text;
    return desired ? format(pixelsToSize(desired[axis], unit, density)) : '';
  };
  const displayedFactor =
    pendingFactor ??
    (original ? requested / Math.max(original.width, original.height) : null);
  const editFactor = (text: string) => {
    const value = Number(text);
    if (!text.trim() || !Number.isFinite(value) || value < 1) {
      setFactorDraft(text);
      setError(copy.factorInvalid);
      return;
    }
    if (!original) {
      setPendingFactor(value);
      setFactorDraft(null);
      setEditingFactor(false);
      setError(null);
      return;
    }
    const factor = Math.min(value, maxFactor);
    if (factor < 1) {
      setError(copy.limit);
      return;
    }
    if (commit(Math.max(original.width, original.height) * factor)) {
      setFactorDraft(null);
      setEditingFactor(false);
      if (value > maxFactor)
        toast.info(
          copy.factorAdjusted.replace('{factor}', `${format(maxFactor)}×`)
        );
    }
  };
  useEffect(() => {
    if (pendingFactor !== null && original && source) {
      setPendingFactor(null);
      editFactor(String(pendingFactor));
    }
  }, [
    pendingFactor,
    original?.width,
    original?.height,
    source?.width,
    source?.height,
  ]);
  const dimensions = (value: Dimensions) =>
    `${value.width} × ${value.height} px`;
  const printDimensions = (value: Dimensions) =>
    `${format(pixelsToSize(value.width, unit === 'px' ? 'cm' : unit, density))} × ${format(pixelsToSize(value.height, unit === 'px' ? 'cm' : unit, density))} ${unit === 'px' ? 'cm' : unit} · ${density} DPI`;
  const inputClass =
    'cms-block-workspace-workspace-1-workspace-form-controls__inputControl';
  const prefix = 'cms-block-workspace-workspace-1-workspace-form-controls__';
  const panel = (
    <div className="cms-workspace-upscale-size__editor">
      <div className="cms-workspace-upscale-size__heading">
        <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramLabel">
          {copy.title}
        </span>
        <button
          type="button"
          onClick={closePanel}
          aria-label={copy.close}
          title={copy.close}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
      <fieldset
        className={
          prefix + 'paramSegmentedSlider cms-workspace-upscale-size__modes'
        }
        data-sliding="true"
        style={
          {
            '--workspace-segment-count': 2,
            '--workspace-segment-index': mode === 'custom' ? 0 : 1,
          } as CSSProperties
        }
        aria-label={copy.title}
      >
        {(['custom', 'print'] as const).map((item) => (
          <button
            type="button"
            key={item}
            className={prefix + 'paramSegmentedSliderOption'}
            data-selected={mode === item}
            aria-pressed={mode === item}
            onClick={() => {
              setMode(item);
              setDraft(null);
              setError(null);
              if (item === 'print') setUnit('cm');
            }}
          >
            {copy[item]}
          </button>
        ))}
      </fieldset>
      {mode === 'custom' ? (
        <>
          <div className="cms-workspace-upscale-size__dimensions">
            {(['width', 'height'] as const).map((axis) => (
              <label key={axis} htmlFor={`${id}-${axis}`}>
                {copy[axis]}
                <input
                  id={`${id}-${axis}`}
                  className={inputClass}
                  type="number"
                  min="0.01"
                  step="any"
                  disabled={!original}
                  value={fieldValue(axis)}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? `${id}-error` : undefined}
                  onChange={(event) => editDimension(axis, event.target.value)}
                />
              </label>
            ))}
            <label htmlFor={`${id}-unit`}>
              {copy.unit}
              <Select
                value={unit}
                onValueChange={(value) => {
                  setUnit(value as UpscalerSizeUnit);
                  setDraft(null);
                  setError(null);
                }}
              >
                <SelectTrigger
                  id={`${id}-unit`}
                  className={prefix + 'paramSelectTrigger'}
                  aria-label={copy.unit}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  portal={false}
                  className={prefix + 'paramSelectContent'}
                  onEscapeKeyDown={(event) => event.stopPropagation()}
                >
                  {(['px', 'mm', 'cm', 'in'] as const).map((value) => (
                    <SelectItem
                      key={value}
                      value={value}
                      className={prefix + 'paramSelectItem'}
                    >
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>
          {displayedFactor !== null && (
            <div className="cms-workspace-upscale-size__heading cms-workspace-upscale-size__note">
              <span>{copy.factor}</span>
              <span>{format(displayedFactor)}×</span>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="cms-workspace-upscale-size__presetList">
            {(['posters', 'photos', 'paper'] as const).map((group) => (
              <div key={group}>
                <h4>{copy[group]}</h4>
                {UPSCALER_PRINT_PRESETS.filter(
                  (item) => item.group === group
                ).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={presetId === item.id}
                    disabled={!original}
                    onClick={() => applyPreset(item.id)}
                  >
                    <span>{item.label}</span>
                    <span>
                      {format(
                        item.unit === 'in' ? item.width * 2.54 : item.width / 10
                      )}{' '}
                      ×{' '}
                      {format(
                        item.unit === 'in'
                          ? item.height * 2.54
                          : item.height / 10
                      )}{' '}
                      cm
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
      {(error || exceedsLimit) && (
        <p id={`${id}-error`} role="alert">
          {error ?? copy.limit}
        </p>
      )}

      {!original && (
        <output>{inputImageUrl ? copy.loading : copy.upload}</output>
      )}
    </div>
  );
  const factorIndex = editingFactor
    ? 4
    : displayedFactor === null
      ? -1
      : [2, 4, 6, 8].includes(displayedFactor)
        ? [2, 4, 6, 8].indexOf(displayedFactor)
        : 4;
  const selectedIndex = values[UPSCALER_CUSTOM_SIZE_FIELD]
    ? -1
    : (param.options?.findIndex(
        (option) => option.value === values[param.id]
      ) ?? -1);
  return (
    <section
      ref={rootRef}
      className="cms-workspace-upscale-size__root"
      aria-label={copy.title}
    >
      <div className="cms-workspace-upscale-size__heading">
        <span className={prefix + 'paramLabel'}>{copy.title}</span>
        <div
          className={
            prefix +
            'paramSegmentedSlider cms-workspace-upscale-size__sizeModes'
          }
          data-sliding="true"
          style={
            {
              '--workspace-segment-count': 2,
              '--workspace-segment-index': sizeMode === 'target' ? 0 : 1,
            } as CSSProperties
          }
        >
          {(['target', 'factor'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={prefix + 'paramSegmentedSliderOption'}
              aria-pressed={sizeMode === value}
              onClick={() => {
                if (value === 'target') {
                  const selectedTarget = (param.options ?? []).find((option) =>
                    Object.is(option.value, values[param.id])
                  );
                  const target =
                    selectedTarget && !isTargetOptionUnavailable(selectedTarget)
                      ? selectedTarget
                      : getFallbackTargetOption(selectedTarget);
                  if (
                    target &&
                    typeof target.value === 'string' &&
                    commit(
                      TARGET_LONG_EDGE_BY_TIER[
                        target.value as keyof typeof TARGET_LONG_EDGE_BY_TIER
                      ],
                      false
                    )
                  ) {
                    setSizeMode(value);
                    if (target !== selectedTarget) {
                      toast.info(
                        copy.factorAdjusted.replace('{factor}', target.label)
                      );
                    }
                  }
                } else {
                  setSizeMode(value);
                  if (original)
                    editFactor(String(Math.max(1, displayedFactor ?? 1)));
                }
              }}
            >
              {value === 'target' ? copy.targetMode : copy.factorMode}
            </button>
          ))}
        </div>
      </div>
      {sizeMode === 'factor' ? (
        <fieldset
          className={
            prefix +
            'paramSegmentedSlider cms-workspace-upscale-size__sizeOptions cms-workspace-upscale-size__factorControls'
          }
          data-sliding={factorIndex >= 0 ? 'true' : undefined}
          style={
            {
              '--workspace-segment-count': 5,
              '--workspace-segment-index': factorIndex,
            } as CSSProperties
          }
          aria-label={copy.factorMode}
        >
          {[2, 4, 6, 8].map((factor) => {
            const disabled = Boolean(original && factor > maxFactor);
            return (
              <div
                key={factor}
                className={prefix + 'paramSegmentedSliderOption'}
                data-disabled={disabled ? 'true' : undefined}
                data-selected={!editingFactor && displayedFactor === factor}
              >
                <button
                  type="button"
                  disabled={disabled}
                  className={
                    prefix +
                    'paramSegmentedSliderOptionLabel cms-workspace-upscale-size__factorOptionButton'
                  }
                  aria-pressed={!editingFactor && displayedFactor === factor}
                  onClick={() => editFactor(String(factor))}
                >
                  {factor}×
                </button>
                {disabled ? renderUnavailableHint() : null}
              </div>
            );
          })}
          <div
            className={prefix + 'paramSegmentedSliderOption'}
            data-disabled={original && maxFactor < 1 ? 'true' : undefined}
            data-selected={
              editingFactor ||
              (displayedFactor !== null &&
                ![2, 4, 6, 8].includes(displayedFactor))
            }
          >
            {editingFactor ? (
              <label className="cms-workspace-upscale-size__factorInput">
                <input
                  ref={factorInputRef}
                  type="number"
                  min="1"
                  max={original ? maxFactor : undefined}
                  step="0.01"
                  aria-label={copy.factorMode}
                  aria-invalid={Boolean(error)}
                  value={factorDraft ?? ''}
                  onChange={(event) => {
                    setFactorDraft(event.target.value);
                    setError(null);
                  }}
                  onBlur={(event) => editFactor(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                    if (event.key === 'Escape') {
                      setEditingFactor(false);
                      setFactorDraft(null);
                      setError(null);
                    }
                  }}
                />
                <span aria-hidden="true">×</span>
              </label>
            ) : (
              <button
                type="button"
                disabled={Boolean(original && maxFactor < 1)}
                className={
                  prefix +
                  'paramSegmentedSliderOptionLabel cms-workspace-upscale-size__customFactor'
                }
                aria-label={copy.custom}
                onClick={() => {
                  setFactorDraft(
                    displayedFactor ? format(displayedFactor) : ''
                  );
                  setEditingFactor(true);
                }}
              >
                {displayedFactor !== null &&
                ![2, 4, 6, 8].includes(displayedFactor)
                  ? `${format(displayedFactor)}×`
                  : copy.custom}
              </button>
            )}
            {original && maxFactor < 1 ? renderUnavailableHint() : null}
          </div>
        </fieldset>
      ) : (
        <fieldset
          className={
            prefix +
            'paramSegmentedSlider cms-workspace-upscale-size__sizeOptions'
          }
          aria-label={copy.quick}
          data-sliding={selectedIndex >= 0 ? 'true' : undefined}
          style={
            {
              '--workspace-segment-count': param.options?.length,
              '--workspace-segment-index': selectedIndex,
            } as CSSProperties
          }
        >
          {param.options?.map((option) => {
            const selected =
              !values[UPSCALER_CUSTOM_SIZE_FIELD] &&
              values[param.id] === option.value;
            const disabled = isTargetOptionUnavailable(option);
            return (
              <div
                key={String(option.value)}
                className={prefix + 'paramSegmentedSliderOption'}
                data-disabled={disabled ? 'true' : undefined}
                data-selected={selected ? 'true' : 'false'}
              >
                <label className="flex min-w-0 flex-1 cursor-pointer items-center justify-center">
                  <input
                    className={prefix + 'paramSegmentedSliderInput'}
                    type="radio"
                    name={id + '-quick'}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => {
                      if (
                        commit(
                          TARGET_LONG_EDGE_BY_TIER[
                            option.value as keyof typeof TARGET_LONG_EDGE_BY_TIER
                          ],
                          false
                        )
                      ) {
                        setDraft(null);
                        setPresetId('');
                      }
                    }}
                  />
                  <span className={prefix + 'paramSegmentedSliderOptionLabel'}>
                    {option.label}
                  </span>
                </label>
                {showPaidFeatureMarkers && option.requiredPaid && (
                  <PremiumCrownIcon
                    className={prefix + 'paramSegmentedSliderPremiumIcon'}
                    iconClassName={
                      prefix + 'paramSegmentedSliderPremiumIconGlyph'
                    }
                    tooltipText={premiumFeatureTooltip ?? option.label}
                  />
                )}
                {disabled ? renderUnavailableHint() : null}
              </div>
            );
          })}
        </fieldset>
      )}
      <div className="cms-workspace-upscale-size__entryRow">
        <button
          ref={triggerRef}
          type="button"
          className="cms-workspace-upscale-size__trigger"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={id + '-editor'}
          onClick={() => (open ? closePanel() : setOpen(true))}
        >
          {copy.entry}
        </button>
      </div>
      {open && !isMobile && (
        <dialog
          ref={popoverRef}
          style={
            popoverTop === null ? { visibility: 'hidden' } : { top: popoverTop }
          }
          id={id + '-editor'}
          open
          aria-label={copy.title}
          className="cms-block-workspace-workspace-1-workspace-tool-header__popover cms-workspace-upscale-size__popover"
        >
          {panel}
        </dialog>
      )}
      {isMobile && (
        <WorkspaceMobileDrawer
          id={id + '-editor'}
          open={open}
          onOpenChange={(next) => (next ? setOpen(true) : closePanel())}
          title={copy.title}
          header="sr-only"
        >
          {panel}
        </WorkspaceMobileDrawer>
      )}
      {!open && (error || exceedsLimit) && (
        <p role="alert">{error ?? copy.limit}</p>
      )}
      {original &&
        imageInfoContainer &&
        createPortal(
          <WorkspaceImageInfoCard
            rows={[
              { id: 'input', label: copy.input, value: dimensions(original) },
              ...(showActual && result
                ? [
                    {
                      id: 'result',
                      label: copy.actual,
                      value: actualResultDimensions
                        ? dimensions(actualResultDimensions)
                        : result.loading
                          ? copy.resultLoading
                          : copy.resultError,
                    },
                    ...(actualResultDimensions
                      ? [
                          {
                            id: 'print',
                            label: copy.printSize,
                            value: printDimensions(actualResultDimensions),
                          },
                        ]
                      : []),
                  ]
                : output && !error && !exceedsLimit
                  ? [
                      {
                        id: 'estimate',
                        label: copy.output,
                        value: dimensions(output),
                      },
                      {
                        id: 'print',
                        label: copy.printSize,
                        value: printDimensions(output),
                      },
                    ]
                  : []),
            ]}
          />,
          imageInfoContainer
        )}
    </section>
  );
}
